import type { FC, ReactNode } from 'react'

import { useEffect, useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { Menu, Search, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Logo } from '@/components/generals/logo'

import { mainNav } from '@/constants/site'

const isActivePath = (pathname: string, href: string): boolean => {
    if (pathname === href || pathname.startsWith(`${href}/`)) return true

    return href === '/compare' && pathname.startsWith('/alternatives')
}

export const SiteHeader: FC = (): ReactNode => {
    const router = useRouter()
    const [open, setOpen] = useState(false)

    useEffect(() => {
        const close = () => setOpen(false)

        router.events.on('routeChangeStart', close)

        return () => router.events.off('routeChangeStart', close)
    }, [router.events])

    return (
        <header className='sticky top-0 z-40 border-b border-border/70 bg-background/80 backdrop-blur-xl'>
            <div className='mx-auto flex h-16 max-w-7xl items-center justify-between gap-6 px-4 sm:px-6 lg:px-8'>
                <Link
                    href='/'
                    aria-label='Framerank home'
                    className='rounded-lg focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary'
                >
                    <Logo />
                </Link>

                <nav aria-label='Main' className='hidden items-center gap-1 md:flex'>
                    {mainNav.map(item => {
                        const active = isActivePath(router.pathname, item.href)

                        return (
                            <Link
                                key={item.href}
                                href={item.href}
                                aria-current={active ? 'page' : undefined}
                                className={cn(
                                    'rounded-full px-3.5 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground focus-visible:outline-2 focus-visible:outline-primary',
                                    active && 'bg-secondary text-foreground'
                                )}
                            >
                                {item.label}
                            </Link>
                        )
                    })}
                </nav>

                <div className='flex items-center gap-2'>
                    <Link
                        href='/tools'
                        className='hidden items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-transform hover:-translate-y-px focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary sm:inline-flex'
                    >
                        <Search className='size-4' aria-hidden='true' />
                        Find a tool
                    </Link>

                    <button
                        type='button'
                        onClick={() => setOpen(value => !value)}
                        aria-expanded={open}
                        aria-controls='mobile-nav'
                        aria-label={open ? 'Close menu' : 'Open menu'}
                        className='inline-flex size-10 items-center justify-center rounded-full border border-border text-foreground transition-colors hover:bg-secondary focus-visible:outline-2 focus-visible:outline-primary md:hidden'
                    >
                        {open ? <X className='size-5' aria-hidden='true' /> : <Menu className='size-5' aria-hidden='true' />}
                    </button>
                </div>
            </div>

            {open && (
                <nav
                    id='mobile-nav'
                    aria-label='Mobile'
                    className='animate-in fade-in slide-in-from-top-2 border-t border-border/70 px-4 pb-5 pt-3 duration-200 md:hidden'
                >
                    <ul className='flex flex-col gap-1'>
                        {mainNav.map(item => {
                            const active = isActivePath(router.pathname, item.href)

                            return (
                                <li key={item.href}>
                                    <Link
                                        href={item.href}
                                        aria-current={active ? 'page' : undefined}
                                        className={cn(
                                            'block rounded-xl px-4 py-3 text-base font-medium text-muted-foreground hover:bg-secondary hover:text-foreground',
                                            active && 'bg-secondary text-foreground'
                                        )}
                                    >
                                        {item.label}
                                    </Link>
                                </li>
                            )
                        })}
                    </ul>

                    <Link
                        href='/tools'
                        className='mt-3 flex items-center justify-center gap-2 rounded-xl bg-primary px-4 py-3 text-sm font-semibold text-primary-foreground'
                    >
                        <Search className='size-4' aria-hidden='true' />
                        Find a tool
                    </Link>
                </nav>
            )}
        </header>
    )
}
